import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import MicRecorder from './MicRecorder'
import styles from './styles.module.scss'
import useTranslationFunction from '../../hooks/useTranslationFunction'
import { runtime } from '@deltachat-desktop/runtime-interface'
import useDialog from '../../hooks/dialog/useDialog'
import AlertDialog from '../dialogs/AlertDialog'
import { getLogger } from '@deltachat-desktop/shared/logger'
import SettingsStoreInstance, { useSettingsStore } from '../../stores/settings'
import { MediaPlayerMutexContext } from '../../contexts/MediaPlayerMutexContext'
import { drawBars } from '../AudioPlayer/Waveform'
import { CustomAudioControls } from '../AudioPlayer'
import { IconButton } from '../Icon'
import OutsideClickHelper from '../OutsideClickHelper'
import {
  CANCEL_DX,
  classifyGesture,
  deviceLabel,
  resolveDeviceId,
  silenceReset,
  silenceStep,
  timerElapsedMs,
  timerPause,
  timerResume,
  timerStart,
  type SilenceState,
  type TimerState,
} from './recorderLogic'

const log = getLogger('renderer/AudioRecorder')

const HISTORY = 64

export enum AudioErrorType {
  'NO_INPUT',
  'OTHER_ERROR',
}

export class AudioRecorderError extends Error {
  public errorType: AudioErrorType
  constructor(message: string, type = AudioErrorType.OTHER_ERROR) {
    super(message)
    this.errorType = type
  }
}

/**
 * shows a timer in format MM:SS. `paused` freezes the count without losing the
 * elapsed active time (wall-clock accumulator, see recorderLogic).
 */
const Timer = ({ paused = false }: { paused?: boolean }) => {
  // Pure initial value (no Date.now during render); the mount effect starts it.
  const stateRef = useRef<TimerState>({ accumulatedMs: 0, runningSince: null })
  const [totalSeconds, setTotalSeconds] = useState(0)
  const tick = () =>
    setTotalSeconds(
      Math.floor(timerElapsedMs(stateRef.current, Date.now()) / 1000)
    )

  // Start on mount; re-read the accumulator a few times a second so the running
  // segment ticks up. While paused the accumulator ignores `now` → frozen.
  useEffect(() => {
    stateRef.current = timerStart(Date.now())
    const timer = window.setInterval(tick, 250)
    return () => window.clearInterval(timer)
  }, [])

  // Apply pause/resume transitions to the accumulator as the prop flips, and
  // reflect the freeze/resume in the display immediately.
  useEffect(() => {
    stateRef.current = paused
      ? timerPause(stateRef.current, Date.now())
      : timerResume(stateRef.current, Date.now())
    tick()
  }, [paused])

  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, '0')
  const seconds = (totalSeconds % 60).toString().padStart(2, '0')

  return (
    <div role='timer'>
      <p>
        {minutes}:{seconds}
      </p>
    </div>
  )
}

/**
 * Show a volume meter by stacking 3 div layers
 * 1. a mask to show a grid of steps
 * 2. a level layer to show/hide the coloured background
 * 3. a coloured background with a color gradient from green to red
 */
const VolumeMeter = (prop: { volume: number; compact?: boolean }) => {
  const steps = 10
  // doubling the volume shows a more realistic volume level
  const level = Math.min(prop.volume * 2, 1)
  const levelPercentage = `${(1 - level) * 100}%`

  return (
    <div
      role='meter'
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={level}
      className={
        prop.compact ? styles.volumeBarCompact : styles.volumeBarContainer
      }
    >
      <div className={styles.volumeBar}>
        <div className={styles.mask}>
          {Array.from({ length: steps }).map((_, index) => (
            <div key={index} className={styles.step} />
          ))}
        </div>
        <div className={styles.level} style={{ width: levelPercentage }} />
        <div className={styles.colorBackground} />
      </div>
    </div>
  )
}

export const AudioRecorder = ({
  recording,
  setRecording,
  saveVoiceAsDraft,
  focusMessageInput,
  onError,
}: {
  recording: boolean
  setRecording: (f: boolean) => void
  saveVoiceAsDraft: (blob: Blob) => void
  focusMessageInput: () => void
  onError: (error: AudioRecorderError) => void
}) => {
  const tx = useTranslationFunction()
  const [volume, setVolume] = useState(0)
  const recorder = useRef<MicRecorder | null>(null)
  const { openDialog } = useDialog()
  const mutexCtx = useContext(MediaPlayerMutexContext)

  // Bumped on every start attempt and on cancel/stop. An async start reads its
  // generation at entry and bails after each await / before opening the mic if
  // it's been superseded — so a gesture that cancels or stops during the
  // permission await can't resume and re-open the mic against the user's
  // intent, and a double pointerdown can't leave two MicRecorders running.
  const startGenRef = useRef(0)
  // The in-flight press-and-hold gesture's window-listener teardown, so the
  // unmount effect can run it if the component leaves mid-gesture.
  const gestureCleanupRef = useRef<(() => void) | null>(null)

  // The whole phase-B polish (pause/resume, preview, gestures, original audio)
  // rides on the SAME experimental setting as the custom player. Off → today's
  // recorder, byte-identical.
  const settings = useSettingsStore()[0]?.desktopSettings
  const experimental = settings?.experimentalAudioPlayerControls === true
  const originalAudio = settings?.voiceRecordingOriginalAudio === true
  const voiceRecordingDeviceId = settings?.voiceRecordingDeviceId ?? null
  const liveWaveform = experimental

  // Microphone picker (experimental). Enumerated while the recording row is up
  // (permission is granted then, so labels populate); refreshed on each open.
  // Rendered only when there's more than one audioinput.
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([])
  const [micMenuOpen, setMicMenuOpen] = useState(false)
  // Wraps trigger + menu; used to hand focus back to the trigger button.
  // ponytail: IconButton isn't a forwardRef, so focus the trigger by its
  // data-testid inside this wrapper rather than reffing the button directly.
  const micPickerRef = useRef<HTMLDivElement>(null)
  // Stable so OutsideClickHelper doesn't re-register its document listeners on
  // every volume-driven render (~12/s while the menu is open). Outside-click
  // just closes; focus stays where the click landed.
  const closeMicMenu = useCallback(() => setMicMenuOpen(false), [])
  // Escape / select close the menu AND return focus to the trigger.
  const closeMicMenuToTrigger = useCallback(() => {
    setMicMenuOpen(false)
    micPickerRef.current
      ?.querySelector<HTMLButtonElement>('[data-testid="mic-picker-trigger"]')
      ?.focus()
  }, [])
  // deviceId actually feeding the open stream (from track.getSettings), captured
  // when the stream opens / after a hot-switch so the checkmark doesn't read the
  // recorder ref during render. null until the first stream opens.
  const [appliedDeviceId, setAppliedDeviceId] = useState<string | null>(null)

  // Inline no-sound warning (experimental): the SilenceDetector drives it from
  // the per-frame RMS instead of the OFF path's aborting 1s NO_INPUT alert.
  const silenceRef = useRef<SilenceState>(silenceReset())
  const [noSound, setNoSound] = useState(false)

  // Recording pause state (experimental).
  const [paused, setPaused] = useState(false)
  // Preview-before-send: after stopping, hold the recorded blob and play it
  // locally through the shared CustomAudioControls until the user sends /
  // re-records / discards. The object URL is revoked on every transition.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const previewUrlRef = useRef<string | null>(null)
  const previewBlobRef = useRef<Blob | null>(null)
  const previewAudioRef = useRef<HTMLAudioElement>(null)

  // Press-and-hold gesture state (experimental). `holding` = pointer is down
  // mid-gesture (show lock/cancel affordances); once released as a tap or
  // locked, we drop to the tap-to-stop "latched" row.
  const [holding, setHolding] = useState(false)
  const [slideDx, setSlideDx] = useState(0) // leftward pointer delta, ≤ 0
  const [cancelArmed, setCancelArmed] = useState(false)

  const historyRef = useRef(new Float32Array(HISTORY))
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Drive the rolling waveform straight from the recorder's per-buffer volume
  // callback (~85ms) and draw imperatively — NOT from a [volume] effect: React
  // bails out when volume is unchanged (digital silence = exactly 0), so an
  // effect would never re-run and the waveform would freeze instead of scroll.
  const pushVolume = useCallback(
    (v: number) => {
      setVolume(v) // drives the meter aria-valuenow
      const h = historyRef.current
      h.copyWithin(0, 1) // shift left, newest at the right
      h[HISTORY - 1] = Math.min(v * 2, 1) // same scaling as VolumeMeter
      if (canvasRef.current != null) {
        // Only non-null while the setting is on and recording, so no gate needed.
        drawBars(canvasRef.current, h, 1)
      }
      // Experimental only: paused frames never reach here (onaudioprocess skips
      // them), so paused time can't accumulate as silence. OFF keeps the aborting
      // 1s alert instead, so we skip the detector to stay byte-identical. The
      // recorder captures this closure at construction (per take), so a mid-take
      // settings flip is picked up on the next recording — fine, it's rare.
      if (experimental) {
        const [next, warn] = silenceStep(silenceRef.current, v, Date.now())
        silenceRef.current = next
        setNoSound(prev => (prev === warn ? prev : warn))
      }
    },
    [experimental]
  )

  const onAccessDenied = useCallback(() => {
    openDialog(AlertDialog, {
      message: tx('perm_explain_access_to_mic_denied'),
    })
  }, [openDialog, tx])

  const cleanupPreview = useCallback(() => {
    if (previewUrlRef.current != null) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
    previewBlobRef.current = null
    setPreviewUrl(null)
  }, [])

  const onRecordingStart = async () => {
    // Snapshot this start's generation; a cancel/stop or a newer start bumps
    // the counter and we bail rather than open the mic against user intent.
    const gen = ++startGenRef.current
    let access = 'unknown'
    try {
      access = await runtime.checkMediaAccess('microphone')
      if (gen !== startGenRef.current) {
        return // superseded during the permission check
      }
      log.debug('checkMediaAccess', { access })
      if (access === 'denied') {
        onAccessDenied()
        return
      }
      if (access === 'not-determined') {
        // try to ask for access
        const accessAllowed = await runtime.askForMediaAccess('microphone')
        if (gen !== startGenRef.current) {
          return // superseded while the permission prompt was open
        }
        if (accessAllowed === false) {
          onAccessDenied()
          return
        }
      }
    } catch (err: any) {
      log.error('failed to check or request media permission', err)
      onError(new AudioRecorderError(err.message))
      return
    }

    if (gen !== startGenRef.current) {
      return // superseded after the awaits, before touching the mic
    }
    // Rebuild per recording so the current originalAudio setting takes effect
    // (getUserMedia is called fresh in start(), so the choice applies here).
    // Gated on `experimental` so OFF mode always requests default constraints
    // (audio: true) — byte-identical to today regardless of the stored setting.
    const mic = new MicRecorder(pushVolume, {
      bitRate: 32,
      originalAudio: experimental && originalAudio,
      // Persisted picker choice (experimental only, so OFF stays default audio:
      // true → byte-identical). A stale id is caught in start() and reset below.
      deviceId: experimental ? voiceRecordingDeviceId : null,
    })
    recorder.current = mic
    setPaused(false)
    setNoSound(false)
    setAppliedDeviceId(null)
    setMicMenuOpen(false) // don't reopen a menu left open from a prior take
    silenceRef.current = silenceReset()
    historyRef.current.fill(0) // don't carry the previous session's tail
    setRecording(true)
    mic
      .start()
      .then(() => {
        if (gen !== startGenRef.current) {
          // Cancelled/stopped while getUserMedia was resolving: close the
          // freshly opened stream instead of leaving the mic live.
          mic.stop()
          return
        }
        // Stale persisted deviceId got dropped to the system default in start():
        // clear the setting so the picker doesn't keep pointing at a gone mic.
        if (mic.deviceIdWasStale) {
          // undefined (not null): the persistence setter only round-trips
          // string|number|boolean|undefined. Reads normalize back with `?? null`.
          SettingsStoreInstance.effect.setDesktopSetting(
            'voiceRecordingDeviceId',
            undefined
          )
        }
        // Capture the real device now feeding the stream (may differ from the
        // persisted id after a stale fallback) so the picker checkmark is right.
        setAppliedDeviceId(mic.getAppliedSettings()?.deviceId ?? null)
        // OFF path only: the aborting 1s NO_INPUT alert. ON replaces it with the
        // non-aborting inline SilenceDetector warning (user can switch mics).
        if (!experimental) {
          window.setTimeout(() => {
            if (!recorder.current?.audioSignalDetected && recording) {
              onError(
                new AudioRecorderError('No input', AudioErrorType.NO_INPUT)
              )
              onRecordingCancel()
            }
          }, 1000)
        }
      })
      .catch((err: any) => {
        onError(new AudioRecorderError(err.message))
        setRecording(false)
      })
  }

  const onRecordingStop = () => {
    startGenRef.current++ // invalidate any in-flight start
    setTimeout(() => focusMessageInput())

    const mp3 = recorder.current?.stop().getMp3()
    if (mp3 == null) {
      // No recorder (e.g. released before permission resolved): nothing to save.
      setRecording(false)
      return
    }
    mp3
      .then(([buffer, _blob]) => {
        const blob = new Blob(buffer as any, { type: 'audio/mp3' })
        if (experimental) {
          // Don't draft yet — show the preview row (keep `recording` true so the
          // composer bar stays ours). Send/discard from there.
          const url = URL.createObjectURL(blob)
          if (previewUrlRef.current != null) {
            URL.revokeObjectURL(previewUrlRef.current)
          }
          previewUrlRef.current = url
          previewBlobRef.current = blob
          setHolding(false)
          setPaused(false)
          setPreviewUrl(url)
        } else {
          setRecording(false)
          saveVoiceAsDraft(blob)
        }
      })
      .catch((err: any) => {
        onError(new AudioRecorderError(err.message))
        setRecording(false)
      })
  }

  const onRecordingCancel = useCallback(() => {
    startGenRef.current++ // invalidate any in-flight start
    setRecording(false)
    setHolding(false)
    recorder.current?.stop()
  }, [setRecording])

  const togglePause = () => {
    const r = recorder.current
    if (r == null) {
      return
    }
    if (paused) {
      // Reset silence tracking on resume so the paused gap can't back-date
      // quietSince and instant-warn (paused frames were never fed).
      silenceRef.current = silenceReset()
      setNoSound(false)
      r.resume()
      setPaused(false)
    } else {
      setNoSound(false) // no signal while paused isn't "no sound"
      r.pause()
      setPaused(true)
    }
  }

  // ponytail: no `devicechange` listener — reopening the menu re-enumerates,
  // which is enough for the recorder's short-lived lifetime. Upgrade path is a
  // navigator.mediaDevices.ondevicechange refresh if hot-plug during a take
  // needs to reflow the list live.
  const refreshDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      setAudioInputs(devices.filter(d => d.kind === 'audioinput'))
    } catch (err: any) {
      log.error('enumerateDevices failed', err)
    }
  }, [])

  // Persist the choice and, if recording, hot-swap without breaking the take.
  const selectDevice = (deviceId: string | null) => {
    closeMicMenuToTrigger()
    // null → undefined for the persistence setter (see stale-reset note above).
    SettingsStoreInstance.effect.setDesktopSetting(
      'voiceRecordingDeviceId',
      deviceId ?? undefined
    )
    const r = recorder.current
    r?.switchDevice(deviceId)
      .then(() => setAppliedDeviceId(r.getAppliedSettings()?.deviceId ?? null))
      .catch((err: any) => log.error('switchDevice failed', err))
  }

  const onPreviewSend = () => {
    const blob = previewBlobRef.current
    cleanupPreview()
    setRecording(false)
    setTimeout(() => focusMessageInput())
    if (blob != null) {
      saveVoiceAsDraft(blob)
    }
  }
  const onPreviewRerecord = () => {
    cleanupPreview()
    // keep `recording` true; start a fresh recording immediately
    onRecordingStart()
  }

  // --- press-and-hold gesture (experimental) --------------------------------
  // Window-level listeners (not setPointerCapture) so tracking survives the
  // mic button unmounting when the recording row replaces it.
  // ponytail: window listeners instead of setPointerCapture for that reason;
  // upgrade path is a stable button element if we ever want native capture.
  const onMicPointerDown = (e: React.PointerEvent) => {
    if (!experimental || e.button !== 0) {
      return
    }
    const startX = e.clientX
    const startY = e.clientY
    const startT = Date.now()
    let active = true

    setHolding(true)
    setSlideDx(0)
    setCancelArmed(false)

    // Suppress the OS long-press context menu for the WHOLE gesture: the mic
    // button unmounts ~100ms into a hold (its own onContextMenu goes with it),
    // but the ~500ms touch long-press would then pop a menu over the recording
    // row. A window-level listener covers that gap until the gesture ends.
    const suppressContextMenu = (ev: Event) => ev.preventDefault()
    const cleanup = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      window.removeEventListener('contextmenu', suppressContextMenu)
      gestureCleanupRef.current = null
    }
    gestureCleanupRef.current = cleanup
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - startX
      const dy = ev.clientY - startY
      setSlideDx(Math.min(dx, 0))
      setCancelArmed(dx <= CANCEL_DX)
      // Reuse the release path's precedence: classifyGesture checks cancel
      // before lock, so a diagonal up-AND-left flick stays a cancel (finalized
      // on `up`) instead of latching a lock the user was trying to throw away.
      // Live-lock only when the gesture classifies as a pure lock.
      if (classifyGesture(dx, dy, Date.now() - startT) === 'lock') {
        // Lock live: hands-free. Stop tracking and drop to the tap-to-stop row.
        active = false
        cleanup()
        setHolding(false)
        setSlideDx(0)
        setCancelArmed(false)
      }
    }
    const up = (ev: PointerEvent) => {
      cleanup()
      if (!active) {
        return
      }
      active = false
      const g = classifyGesture(
        ev.clientX - startX,
        ev.clientY - startY,
        Date.now() - startT
      )
      setHolding(false)
      setSlideDx(0)
      setCancelArmed(false)
      if (g === 'cancel') {
        onRecordingCancel()
      } else if (g === 'hold-release') {
        onRecordingStop()
      }
      // 'tap' / 'lock': recording continues (latched); tap the row's stop
      // button to finish into preview.
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    window.addEventListener('contextmenu', suppressContextMenu)

    onRecordingStart()
  }

  useEffect(() => {
    return () => {
      gestureCleanupRef.current?.() // drop any mid-gesture window listeners
      onRecordingCancel()
      cleanupPreview()
    }
  }, [onRecordingCancel, cleanupPreview])

  useEffect(() => {
    if (!recording) {
      // make sure recorder is stopped when still running
      recorder.current?.stop()
      // and drop any pending preview (e.g. chat switched while previewing).
      // Syncing an external `recording=false` (owned by the composer) into our
      // preview state is exactly what an effect is for, hence the disable.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      cleanupPreview()
    }
  }, [recording, cleanupPreview])

  // Enumerate audioinputs when the recording row is up (permission granted →
  // labels populate). Loading an external list on mount is a valid effect;
  // setAudioInputs only fires after the async enumerate resolves.
  useEffect(() => {
    if (recording && experimental) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      refreshDevices()
    }
  }, [recording, experimental, refreshDevices])

  // Preview playback is mutually exclusive with the shared voice-message
  // singleton: whichever starts pauses the other (pause, not stop, so a
  // mini-player keeps its position). ponytail: the preview deliberately does
  // NOT register as a mutex src — these two listeners are enough for "don't
  // play both at once"; full src wiring (seek/rate mirroring) is the upgrade
  // path if the preview ever needs to drive the singleton.
  useEffect(() => {
    const preview = previewAudioRef.current
    const singleton = mutexCtx.audioElement
    if (previewUrl == null || preview == null) {
      return
    }
    const onPreviewPlay = () => {
      if (!singleton.paused) {
        singleton.pause()
      }
    }
    const onSingletonPlay = () => {
      if (!preview.paused) {
        preview.pause()
      }
    }
    preview.addEventListener('play', onPreviewPlay)
    singleton.addEventListener('play', onSingletonPlay)
    return () => {
      preview.removeEventListener('play', onPreviewPlay)
      singleton.removeEventListener('play', onSingletonPlay)
    }
  }, [previewUrl, mutexCtx.audioElement])

  // --- mic picker + inline no-sound warning (experimental) ------------------
  // Active row = the device actually feeding the stream (getSettings) while
  // recording, else the persisted choice validated against the enumerated list
  // (resolveDeviceId: a stale id resolves to null = system default).
  const activeDeviceId =
    appliedDeviceId ??
    resolveDeviceId(
      voiceRecordingDeviceId,
      audioInputs.map(d => d.deviceId)
    )
  const micCluster = experimental && (
    <div className={styles.micCluster}>
      {noSound && (
        <span
          className={styles.noSoundHint}
          role='status'
          data-testid='recording-no-sound'
        >
          {tx('recording_no_sound')}
        </span>
      )}
      {audioInputs.length > 1 && (
        <div className={styles.micPicker} ref={micPickerRef}>
          <IconButton
            className={styles.micPickerButton}
            icon='devices'
            coloring='currentColor'
            aria-label={tx('select_microphone')}
            aria-haspopup='menu'
            aria-expanded={micMenuOpen}
            data-testid='mic-picker-trigger'
            onClick={() => {
              refreshDevices() // reopen refreshes the list
              setMicMenuOpen(o => !o)
            }}
          />
          {micMenuOpen && (
            <OutsideClickHelper onClick={closeMicMenu}>
              <div
                className={styles.micMenu}
                role='menu'
                aria-label={tx('select_microphone')}
                data-testid='mic-picker-menu'
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    e.stopPropagation()
                    closeMicMenuToTrigger()
                  }
                }}
              >
                {audioInputs.map((d, i) => {
                  const selected = d.deviceId === activeDeviceId
                  const label = deviceLabel(d.label, i)
                  return (
                    <button
                      type='button'
                      role='menuitemradio'
                      aria-checked={selected}
                      key={d.deviceId || i}
                      className={styles.micMenuItem}
                      onClick={() => selectDevice(d.deviceId)}
                    >
                      <span className={styles.micCheck}>
                        {selected ? '✓' : ''}
                      </span>
                      <span className={styles.micLabel} title={label}>
                        {label}
                      </span>
                      {/* ponytail: only the active device is metered — no extra
                          getUserMedia per inactive mic (Jitsi meters the active
                          one too). Reuses the live `volume` state. */}
                      {selected && (
                        <span
                          className={styles.micRowMeter}
                          data-testid='mic-picker-level'
                        >
                          <VolumeMeter volume={volume} compact />
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </OutsideClickHelper>
          )}
        </div>
      )}
    </div>
  )

  // --- preview row ----------------------------------------------------------
  if (previewUrl != null) {
    return (
      <div className={styles.audioRecorder}>
        {/* Not muted and no `controls`: invisible, but produces the sound the
            CustomAudioControls drive. Object-URL peaks work via fetch(); its
            positionStore writes are no-oped (blob: guard). */}
        <audio ref={previewAudioRef} src={previewUrl} preload='metadata' />
        <div className={styles.previewControls}>
          <CustomAudioControls
            audioRef={previewAudioRef}
            src={previewUrl}
            compact
          />
        </div>
        <button
          type='button'
          className={styles.cancel}
          onClick={onPreviewRerecord}
          title={tx('record_again')}
        >
          {tx('record_again')}
        </button>
        <button
          type='button'
          className={styles.cancel}
          onClick={() => {
            cleanupPreview()
            setRecording(false)
          }}
        >
          {tx('delete')}
        </button>
        <button
          type='button'
          className={styles.stopRecording}
          onClick={onPreviewSend}
          aria-label={tx('menu_send')}
          // The message input is unmounted while previewing, so focusing it
          // after stop is a no-op; land focus on Send (the default action).
          autoFocus
        >
          {tx('menu_send')}
        </button>
      </div>
    )
  }

  if (!recording) {
    return (
      <button
        type='button'
        aria-label={tx('voice_message')}
        className={styles.microphoneButton}
        onClick={experimental ? undefined : () => onRecordingStart()}
        onPointerDown={experimental ? onMicPointerDown : undefined}
        // Suppress the long-press context menu so a touch hold records instead
        // of popping a menu (no useful context action on the mic button).
        onContextMenu={experimental ? e => e.preventDefault() : undefined}
      >
        <span />
      </button>
    )
  }

  // recording (possibly holding = mid-gesture, or latched = tap-to-stop)
  return (
    <div className={styles.audioRecorder}>
      {holding && experimental && (
        <div className={styles.holdHints} aria-hidden='true'>
          <div className={styles.lockHint}>🔒 {tx('slide_up_to_lock')}</div>
          <div
            className={styles.cancelHint}
            style={{ transform: `translateX(${slideDx}px)` }}
            data-armed={cancelArmed}
          >
            ‹ {tx('chat_record_slide_to_cancel')}
          </div>
        </div>
      )}
      {experimental && (
        <IconButton
          className={styles.pauseButton}
          icon={paused ? 'play' : 'pause'}
          coloring='currentColor'
          aria-label={paused ? tx('resume_recording') : tx('pause_recording')}
          onClick={togglePause}
        />
      )}
      <button
        type='button'
        className={styles.microphoneButton}
        onClick={() => onRecordingStop()}
        aria-label={tx('stop_recording')}
      >
        <span />
      </button>
      <Timer paused={experimental && paused} />
      {liveWaveform ? (
        <div
          role='meter'
          aria-valuemin={0}
          aria-valuemax={1}
          aria-valuenow={Math.min(volume * 2, 1)}
          className={styles.liveWaveformContainer}
        >
          <canvas
            ref={canvasRef}
            className={styles.liveWaveform}
            aria-hidden='true'
          />
        </div>
      ) : (
        <VolumeMeter volume={volume} />
      )}
      {micCluster}
      {/* display:contents on wide viewports (no layout change); becomes a
          real flex group under 500px so cancel/ok wrap TOGETHER instead of
          stranding ok alone on the next line. */}
      <div className={styles.recActions}>
        <button
          type='button'
          className={styles.cancel}
          onClick={() => onRecordingCancel()}
        >
          {tx('cancel')}
        </button>
        <button
          type='button'
          className={styles.stopRecording}
          onClick={() => onRecordingStop()}
          aria-description={tx('stop_recording')}
        >
          {tx('ok')}
        </button>
      </div>
    </div>
  )
}
